// fiscalImportUtils.ts
// Funções de parsing e validação para importação dos módulos fiscais.
// Nenhuma função lança exceção — retornam null/undefined para valores inválidos.

export interface ValidationError {
  linha: number; // número real no Excel (índice + 2, pois linha 1 é cabeçalho)
  campo: string;
  motivo: string;
}

// ---------------------------------------------------------------------------
// parseDecimal
// Converte qualquer valor para número decimal ou null.
// Aceita: número JS nativo, string com ponto ou vírgula decimal,
//         separadores de milhar BR (1.234,56) e EN (1,234.56).
// Rejeita: strings não numéricas, undefined, null, "".
// Zero é valor válido.
// ---------------------------------------------------------------------------
export function parseDecimal(value: any): number | null {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") {
    return isNaN(value) ? null : value;
  }
  const s = String(value).trim();
  if (s === "") return null;

  // Detectar separador de milhar BR: padrão "1.234,56" ou "1.234"
  // Detectar separador de milhar EN: padrão "1,234.56" ou "1,234"
  let normalized = s;

  const hasDotAndComma = s.includes(".") && s.includes(",");
  if (hasDotAndComma) {
    const dotPos = s.lastIndexOf(".");
    const commaPos = s.lastIndexOf(",");
    if (commaPos > dotPos) {
      // BR: ponto = milhar, vírgula = decimal → "1.234,56" → "1234.56"
      normalized = s.replace(/\./g, "").replace(",", ".");
    } else {
      // EN: vírgula = milhar, ponto = decimal → "1,234.56" → "1234.56"
      normalized = s.replace(/,/g, "");
    }
  } else if (s.includes(",") && !s.includes(".")) {
    // Só vírgula: pode ser decimal BR "18,5" ou milhar "1,234"
    const parts = s.split(",");
    if (parts.length === 2 && parts[1].length <= 2) {
      // Decimal BR: "18,5" ou "18,50"
      normalized = s.replace(",", ".");
    } else if (parts.length === 2 && parts[1].length === 3) {
      // Milhar EN sem decimal: "1,234"
      normalized = s.replace(",", "");
    } else {
      return null; // ambíguo ou inválido
    }
  }
  // Se só ponto: trata como decimal EN padrão (já está correto)

  const result = parseFloat(normalized);
  return isNaN(result) ? null : result;
}

// ---------------------------------------------------------------------------
// parseIntPositive
// Converte para inteiro positivo (>= 1) ou null.
// Zero, negativo, não-numérico, vazio → null.
// Usado para: naturezaOperacaoId, emitenteId, municipioIbgeId.
// ---------------------------------------------------------------------------
export function parseIntPositive(value: any): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = parseInt(String(value).trim(), 10);
  if (isNaN(n) || n <= 0) return null;
  return n;
}

// ---------------------------------------------------------------------------
// parseIntOrNull
// Converte para inteiro >= 0 ou null.
// Zero é válido. Negativo, não-numérico, vazio → null.
// Usado para: origem no PIS-COFINS.
// ---------------------------------------------------------------------------
export function parseIntOrNull(value: any): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = parseInt(String(value).trim(), 10);
  if (isNaN(n) || n < 0) return null;
  return n;
}

// ---------------------------------------------------------------------------
// padCode
// Normaliza código numérico de tamanho fixo com zeros à esquerda.
// Rejeita: não numérico, maior que o tamanho esperado, vazio.
// Aceita: numérico com <= length dígitos → padStart.
// ---------------------------------------------------------------------------
export function padCode(value: any, length: number): string | null {
  if (value === undefined || value === null || value === "") return null;
  const s = String(value).trim().replace(/\s/g, "");
  if (s === "") return null;
  if (!/^\d+$/.test(s)) return null; // não numérico
  if (s.length > length) return null; // maior que esperado
  return s.padStart(length, "0");
}

// ---------------------------------------------------------------------------
// parseNumericString
// Para NCM de varchar(20) e PIS-COFINS NCM: valida que é numérico,
// sem padStart (tamanho não fixo). Retorna string ou null.
// ---------------------------------------------------------------------------
export function parseNumericString(value: any): string | null {
  if (value === undefined || value === null || value === "") return null;
  const s = String(value).trim().replace(/\s/g, "");
  if (s === "") return null;
  if (!/^\d+$/.test(s)) return null;
  return s;
}

// ---------------------------------------------------------------------------
// parseUf
// Valida e normaliza UF para 2 letras maiúsculas.
// Rejeita: não string, tamanho != 2, contém dígitos.
// ---------------------------------------------------------------------------
export function parseUf(value: any): string | null {
  if (value === undefined || value === null || value === "") return null;
  const s = String(value).trim().toUpperCase();
  if (s.length !== 2) return null;
  if (!/^[A-Z]{2}$/.test(s)) return null;
  return s;
}

// ---------------------------------------------------------------------------
// parseExigeGrupo
// Normaliza campo booleano "Exige Grupo".
// true: "Sim","sim","SIM","S","s","true","True",true,1
// false: "Não","nao","NAO","N","n","false","False",false,0,"Nao","NÃO","não"
// null: vazio, undefined, null, qualquer outro valor
// ---------------------------------------------------------------------------
export function parseExigeGrupo(value: any): boolean | null {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  if (value === 1) return true;
  if (value === 0) return false;
  const s = String(value).trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // remove acentos
  if (["sim", "s", "true", "1"].includes(s)) return true;
  if (["nao", "n", "false", "0", "nao"].includes(s)) return false;
  return null;
}

// ---------------------------------------------------------------------------
// validateRows
// Valida todas as linhas de uma importação.
// Retorna array de ValidationError. Se vazio, importação pode prosseguir.
// rowIndex é 0-based; linha Excel = rowIndex + 2.
// ---------------------------------------------------------------------------
export function validateRows(
  rows: any[],
  validateFn: (row: any, rowIndex: number) => ValidationError[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  rows.forEach((row, i) => {
    errors.push(...validateFn(row, i));
  });
  return errors;
}

// ---------------------------------------------------------------------------
// formatValidationErrors
// Formata erros para exibição em toast ou lista.
// ---------------------------------------------------------------------------
export function formatValidationErrors(errors: ValidationError[]): string {
  return errors
    .map((e) => `Linha ${e.linha}: ${e.campo} — ${e.motivo}`)
    .join("\n");
}
